// « À faire » — la liste des choses à ne pas oublier.
//
// Elle est rangée par échéance, jamais à plat : trente lignes d'affilée, on
// n'en lit aucune ; les mêmes trente sous « En retard / Aujourd'hui / Demain »,
// on sait par quoi commencer. Le rangement et le tri viennent de
// @imp/shared/taches, testés à part.
//
// Le geste qui compte est l'ajout : un champ, une phrase, Entrée. Tout le
// reste — échéance, priorité, chantier — est facultatif et se déplie.

import { messageErreur } from '@imp/shared/message-erreur';
import {
  compterTaches,
  grouperTaches,
  jourEcheance,
  LIBELLE_PRIORITE,
  LIBELLE_TRANCHE,
  type PrioriteTache,
  type Tache,
  type Tranche,
} from '@imp/shared/taches';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, ChevronDown, Hammer, Plus, Trash2, User, X } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/modules/auth/AuthContext';
import { ChampsTache, VALEURS_VIDES, type ValeursTache } from '@/modules/taches/ChampsTache';
import {
  changerStatut,
  type ChantierPourTache,
  creerTache,
  listAssignables,
  listChantiersPourTaches,
  listTaches,
  modifierTache,
  supprimerTache,
} from '@/modules/taches/api';

/** La couleur d'une tranche : le retard se voit, le reste se lit. */
const TON_TRANCHE: Record<Tranche, string> = {
  en_retard: 'text-destructive',
  aujourdhui: 'text-foreground',
  demain: 'text-foreground',
  cette_semaine: 'text-muted-foreground',
  plus_tard: 'text-muted-foreground',
  sans_echeance: 'text-muted-foreground',
  fait: 'text-muted-foreground',
};

/** Une priorité ordinaire ne s'affiche pas : seule l'exception mérite un signe. */
const TON_PRIORITE: Partial<Record<PrioriteTache, string>> = {
  urgente: 'bg-destructive/10 text-destructive',
  haute: 'bg-amber-100 text-amber-800',
  basse: 'bg-muted text-muted-foreground',
};

function dateCourte(iso: string | null): string {
  if (iso === null) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' });
}

export function TachesPage() {
  const qc = useQueryClient();
  const { profil } = useAuth();
  const [titre, setTitre] = useState('');
  const [detailsOuverts, setDetailsOuverts] = useState(false);
  const [valeurs, setValeurs] = useState<ValeursTache>(VALEURS_VIDES);
  const [enEdition, setEnEdition] = useState<string | null>(null);
  const [aSupprimer, setASupprimer] = useState<string | null>(null);
  const [seulementMoi, setSeulementMoi] = useState(false);
  const [montrerFaites, setMontrerFaites] = useState(false);

  const taches = useQuery({ queryKey: ['taches'], queryFn: listTaches });
  const chantiers = useQuery({ queryKey: ['taches-chantiers'], queryFn: listChantiersPourTaches });
  const assignables = useQuery({ queryKey: ['taches-assignables'], queryFn: listAssignables });

  const rafraichir = (): void => {
    void qc.invalidateQueries({ queryKey: ['taches'] });
  };

  const ajouter = useMutation({
    mutationFn: () =>
      creerTache(
        {
          titre: titre.trim(),
          description: valeurs.description.trim() === '' ? null : valeurs.description.trim(),
          priorite: valeurs.priorite,
          jourEcheance: valeurs.jourEcheance === '' ? null : valeurs.jourEcheance,
          assigneAId: valeurs.assigneAId === '' ? null : valeurs.assigneAId,
          chantierId: valeurs.chantierId === '' ? null : valeurs.chantierId,
        },
        profil?.id ?? null,
      ),
    onSuccess: () => {
      setTitre('');
      setValeurs(VALEURS_VIDES);
      setDetailsOuverts(false);
      rafraichir();
    },
  });

  // Cocher répond TOUT DE SUITE, avant même que la base ait répondu. Sur un
  // toit, une case qui reste vide une seconde se fait cliquer deux fois — et
  // la tâche repart alors dans l'autre sens. En cas d'échec, l'ancienne liste
  // est remise et l'erreur s'affiche.
  const basculer = useMutation({
    mutationFn: ({ id, fait }: { id: string; fait: boolean }) =>
      changerStatut(id, fait ? 'fait' : 'a_faire'),
    onMutate: async ({ id, fait }: { id: string; fait: boolean }) => {
      await qc.cancelQueries({ queryKey: ['taches'] });
      const avant = qc.getQueryData<Tache[]>(['taches']);
      qc.setQueryData<Tache[]>(['taches'], (liste) =>
        (liste ?? []).map((t) =>
          t.id === id
            ? {
                ...t,
                statut: fait ? ('fait' as const) : ('a_faire' as const),
                completeAt: fait ? new Date().toISOString() : null,
              }
            : t,
        ),
      );
      return { avant };
    },
    onError: (_erreur, _variables, contexte) => {
      if (contexte?.avant !== undefined) qc.setQueryData(['taches'], contexte.avant);
    },
    onSettled: rafraichir,
  });

  const enregistrerEdition = useMutation({
    mutationFn: ({ id, v }: { id: string; v: ValeursTache }) =>
      modifierTache(id, {
        titre: v.titre.trim(),
        description: v.description.trim() === '' ? null : v.description.trim(),
        priorite: v.priorite,
        jourEcheance: v.jourEcheance === '' ? null : v.jourEcheance,
        assigneAId: v.assigneAId === '' ? null : v.assigneAId,
        chantierId: v.chantierId === '' ? null : v.chantierId,
      }),
    onSuccess: () => {
      setEnEdition(null);
      rafraichir();
    },
  });

  const supprimer = useMutation({
    mutationFn: supprimerTache,
    onSuccess: () => {
      setASupprimer(null);
      rafraichir();
    },
  });

  const libelleChantier = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of chantiers.data ?? []) {
      m.set(c.id, c.client === null ? c.libelle : `${c.client} — ${c.libelle}`);
    }
    return m;
  }, [chantiers.data]);

  const { groupes, compteurs } = useMemo(() => {
    const toutes = (taches.data ?? []).map((t) => ({
      ...t,
      entiteLibelle:
        t.entiteType === 'chantier' && t.entiteId !== null
          ? (libelleChantier.get(t.entiteId) ?? null)
          : null,
    }));
    const visibles = toutes
      .filter((t) => !seulementMoi || t.assigneAId === profil?.id)
      .filter((t) => montrerFaites || t.statut !== 'fait');
    return {
      groupes: grouperTaches(visibles),
      // Les compteurs comptent ce qui est VISIBLE : un chiffre qui ne
      // correspond pas à ce qu'on a sous les yeux ne sert à personne.
      compteurs: compterTaches(visibles),
    };
  }, [taches.data, libelleChantier, seulementMoi, montrerFaites, profil?.id]);

  const peutAjouter = titre.trim() !== '' && !ajouter.isPending;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">À faire</h1>
          <p className="text-muted-foreground">
            {compteurs.ouvertes === 0
              ? 'Rien en attente.'
              : `${String(compteurs.ouvertes)} en attente${
                  compteurs.enRetard > 0 ? `, dont ${String(compteurs.enRetard)} en retard` : ''
                }.`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={seulementMoi}
              onChange={(e) => {
                setSeulementMoi(e.target.checked);
              }}
            />
            Seulement les miennes
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={montrerFaites}
              onChange={(e) => {
                setMontrerFaites(e.target.checked);
              }}
            />
            Montrer ce qui est fait
          </label>
        </div>
      </div>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-[16rem] flex-1 space-y-1">
              <Label htmlFor="tache_titre" className="sr-only">
                Que faut-il faire ?
              </Label>
              <Input
                id="tache_titre"
                value={titre}
                placeholder="Que faut-il faire ?"
                onChange={(e) => {
                  setTitre(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && peutAjouter) ajouter.mutate();
                }}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              aria-expanded={detailsOuverts}
              onClick={() => {
                setDetailsOuverts(!detailsOuverts);
              }}
            >
              <ChevronDown
                className={`mr-1.5 h-4 w-4 transition-transform ${detailsOuverts ? 'rotate-180' : ''}`}
              />
              Détails
            </Button>
            <Button
              onClick={() => {
                ajouter.mutate();
              }}
              disabled={!peutAjouter}
            >
              <Plus className="mr-1.5 h-4 w-4" />
              {ajouter.isPending ? 'Ajout…' : 'Ajouter'}
            </Button>
          </div>

          {detailsOuverts ? (
            <ChampsTache
              prefixe="nouvelle"
              valeurs={valeurs}
              onChange={(champs) => {
                setValeurs({ ...valeurs, ...champs });
              }}
              chantiers={chantiers.data ?? []}
              assignables={assignables.data ?? []}
              chargement={chantiers.isLoading}
            />
          ) : null}

          {ajouter.isError ? (
            <p className="text-destructive text-sm">{messageErreur(ajouter.error)}</p>
          ) : null}
        </CardContent>
      </Card>

      {taches.isLoading ? (
        <p className="text-muted-foreground text-sm">Chargement…</p>
      ) : taches.isError ? (
        <p className="text-destructive text-sm">{messageErreur(taches.error)}</p>
      ) : groupes.length === 0 ? (
        <Card>
          <CardContent className="text-muted-foreground py-10 text-center text-sm">
            {seulementMoi
              ? 'Rien ne vous est assigné.'
              : 'Rien à faire pour le moment. Écrivez la première ligne ci-dessus.'}
          </CardContent>
        </Card>
      ) : (
        groupes.map((g) => (
          <Card key={g.tranche}>
            <CardHeader className="pb-2">
              <CardTitle className={`flex items-center gap-2 text-base ${TON_TRANCHE[g.tranche]}`}>
                {g.tranche === 'en_retard' ? <AlertTriangle className="h-4 w-4" /> : null}
                {LIBELLE_TRANCHE[g.tranche]}
                <span className="text-muted-foreground font-normal">({g.taches.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <ul className="divide-y">
                {g.taches.map((t) => (
                  <Fragment key={t.id}>
                    <LigneTache
                      tache={t}
                      enRetard={g.tranche === 'en_retard'}
                      enEdition={enEdition === t.id}
                      aSupprimer={aSupprimer === t.id}
                      onBasculer={(fait) => {
                        basculer.mutate({ id: t.id, fait });
                      }}
                      onEditer={() => {
                        setEnEdition(enEdition === t.id ? null : t.id);
                      }}
                      onDemandeSuppression={setASupprimer}
                      onSupprimer={() => {
                        supprimer.mutate(t.id);
                      }}
                    />
                    {enEdition === t.id ? (
                      <li className="bg-muted/30 px-1 py-4">
                        <EditionTache
                          tache={t}
                          chantiers={chantiers.data ?? []}
                          assignables={assignables.data ?? []}
                          enCours={enregistrerEdition.isPending}
                          onAnnuler={() => {
                            setEnEdition(null);
                          }}
                          onEnregistrer={(v) => {
                            enregistrerEdition.mutate({ id: t.id, v });
                          }}
                        />
                      </li>
                    ) : null}
                  </Fragment>
                ))}
              </ul>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}

function LigneTache({
  tache,
  enRetard,
  enEdition,
  aSupprimer,
  onBasculer,
  onEditer,
  onDemandeSuppression,
  onSupprimer,
}: {
  tache: Tache;
  enRetard: boolean;
  enEdition: boolean;
  aSupprimer: boolean;
  onBasculer: (fait: boolean) => void;
  onEditer: () => void;
  onDemandeSuppression: (id: string | null) => void;
  onSupprimer: () => void;
}) {
  const fait = tache.statut === 'fait';
  return (
    <li className="flex items-start gap-3 py-2.5">
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 shrink-0"
        aria-label={fait ? `Rouvrir « ${tache.titre} »` : `Marquer « ${tache.titre} » comme fait`}
        checked={fait}
        onChange={(e) => {
          onBasculer(e.target.checked);
        }}
      />
      <div className="min-w-0 flex-1">
        <button
          type="button"
          aria-expanded={enEdition}
          className={`block w-full text-left text-sm ${fait ? 'text-muted-foreground line-through' : ''}`}
          onClick={onEditer}
        >
          {tache.titre}
        </button>
        <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          {tache.echeance !== null ? (
            <span className={enRetard ? 'text-destructive font-medium' : ''}>
              {dateCourte(tache.echeance)}
            </span>
          ) : null}
          {tache.priorite !== 'normale' ? (
            <span className={`rounded px-1.5 py-0.5 ${TON_PRIORITE[tache.priorite] ?? ''}`}>
              {LIBELLE_PRIORITE[tache.priorite]}
            </span>
          ) : null}
          {tache.assigneANom !== null ? (
            <span className="flex items-center gap-1">
              <User className="h-3 w-3" />
              {tache.assigneANom}
            </span>
          ) : null}
          {tache.entiteType === 'chantier' && tache.entiteId !== null ? (
            <Link
              to={`/chantiers/${tache.entiteId}`}
              className="hover:text-foreground flex items-center gap-1 hover:underline"
            >
              <Hammer className="h-3 w-3" />
              {tache.entiteLibelle ?? 'Chantier'}
            </Link>
          ) : null}
          {tache.description !== null && tache.description !== '' ? (
            <span className="truncate">{tache.description}</span>
          ) : null}
        </div>
      </div>
      {aSupprimer ? (
        <span className="flex shrink-0 items-center gap-1">
          <Button variant="destructive" size="sm" onClick={onSupprimer}>
            Supprimer
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onDemandeSuppression(null);
            }}
          >
            Non
          </Button>
        </span>
      ) : (
        <button
          type="button"
          aria-label={`Supprimer « ${tache.titre} »`}
          className="text-muted-foreground hover:bg-accent hover:text-destructive shrink-0 rounded p-1.5"
          onClick={() => {
            onDemandeSuppression(tache.id);
          }}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </li>
  );
}

function EditionTache({
  tache,
  chantiers,
  assignables,
  enCours,
  onAnnuler,
  onEnregistrer,
}: {
  tache: Tache;
  chantiers: readonly ChantierPourTache[];
  assignables: readonly { id: string; nom: string }[];
  enCours: boolean;
  onAnnuler: () => void;
  onEnregistrer: (v: ValeursTache) => void;
}) {
  const [v, setV] = useState<ValeursTache>({
    titre: tache.titre,
    description: tache.description ?? '',
    jourEcheance: jourEcheance(tache.echeance),
    priorite: tache.priorite,
    assigneAId: tache.assigneAId ?? '',
    chantierId: tache.entiteType === 'chantier' ? (tache.entiteId ?? '') : '',
  });

  return (
    <div className="space-y-4 px-3">
      <div className="space-y-1">
        <Label htmlFor={`edit_${tache.id}_titre`}>Intitulé</Label>
        <Input
          id={`edit_${tache.id}_titre`}
          value={v.titre}
          onChange={(e) => {
            setV({ ...v, titre: e.target.value });
          }}
        />
      </div>
      <ChampsTache
        prefixe={`edit_${tache.id}`}
        valeurs={v}
        onChange={(champs) => {
          setV({ ...v, ...champs });
        }}
        chantiers={chantiers}
        assignables={assignables}
      />
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          disabled={v.titre.trim() === '' || enCours}
          onClick={() => {
            onEnregistrer(v);
          }}
        >
          <Check className="mr-1.5 h-4 w-4" />
          {enCours ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
        <Button variant="ghost" size="sm" onClick={onAnnuler}>
          <X className="mr-1.5 h-4 w-4" />
          Annuler
        </Button>
      </div>
    </div>
  );
}
